import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { safeArray } from '../../components/ErrorBoundary';
import ProjectFilterSelect from '../../components/ProjectFilterSelect';
import ResetFiltersButton from '../../components/ResetFiltersButton';
import StatusBadge from '../../components/StatusBadge';
import { openPrintWindow } from '../../utils/exportUtils';
import { formatDateIN, formatDateTimeIN, stripBrackets } from '../../utils/dateFormat';
import { formatKpiCrores } from '../../utils/kpiAmountFormat';
import { bankGuaranteesAPI, clearancesAPI, correspondenceAPI, deviationsAPI, drawingsAPI, milestonesAPI, qaqcAPI, raBillsAPI, reportsAPI } from '../../services/authService';
import SeniorManagementReport from './SeniorManagementReport';
import CustomReportBuilder from './CustomReportBuilder';
import SavedCustomReports from './SavedCustomReports';
import { useProjects } from '../../context/ProjectsContext';
import ProjectNameText from '../../components/ProjectNameText';
import KpiBreakdownTooltip from '../../components/KpiBreakdownTooltip';
import { createPermissionChecker } from '../../utils/rbacPermissions';

const BG_STATUS_COLOR_MAP = {
  Active: '#22C55E',
  Expiring: '#F59E0B',
  'Renewal Requested': '#7C3AED',
  Renewed: '#16A34A',
  Released: '#3B82F6',
  Invoked: '#EF4444',
  Closed: '#15803D',
};

const CLEARANCE_STATUS_CLASS_MAP = {
  Draft: 'draft',
  Submitted: 'submitted',
  'Under Review': 'under-review',
  Approved: 'approved',
  Rejected: 'rejected',
  Expired: 'critical',
};

const formatCurrency = (val) => (
  `₹${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(val || 0)}`
);

/** Mirror backend SLA overdue: Open + last letter/update + targeted_reply_days past due. */
const isChainReplyOverdue = (chain) => {
  if (!chain || String(chain.status || '') !== 'Open') return false;
  const targeted = Number(chain.targeted_reply_days ?? chain.targetedReplyDays ?? 0);
  if (!targeted) return false;
  const last = chain.last_entry_date || chain.lastEntryDate || chain.updated_at || chain.updatedAt || chain.created_at || chain.createdAt;
  if (!last) return false;
  const due = new Date(last);
  if (Number.isNaN(due.getTime())) return false;
  due.setDate(due.getDate() + targeted);
  return due < new Date();
};


const Reports = () => {
  const { hasPermission, hasExactPermission } = useMemo(() => createPermissionChecker(), []);
  const hasAnyReportCategoryGrant = [
    'page:summary', 'page:correspondence', 'page:financials', 'page:technical',
    'page:drawings', 'page:senior_management',
  ].some((k) => hasExactPermission('reports', k));
  const canReportPage = (pageKey) => {
    if (hasExactPermission('reports', pageKey)) return true;
    if (hasAnyReportCategoryGrant) return false;
    if (['page:senior_management', 'page:custom_report_builder', 'page:saved_custom_reports'].includes(pageKey)) return false;
    return hasPermission('reports', 'view');
  };
  const canSummary = canReportPage('page:summary');
  const canCorrespondence = canReportPage('page:correspondence');
  const canFinancials = canReportPage('page:financials');
  const canTechnical = canReportPage('page:technical');
  const canDrawings = canReportPage('page:drawings');
  const canSeniorManagement = canReportPage('page:senior_management');
  const canCustomBuilder = hasPermission('reports', 'page:custom_report_builder') && canReportPage('page:custom_report_builder');
  const canSavedReports = hasPermission('reports', 'page:saved_custom_reports') && canReportPage('page:saved_custom_reports');
  const allowedTabs = [
    canSummary && 'summary',
    canCorrespondence && 'correspondence',
    canFinancials && 'financials',
    canTechnical && 'technical',
    canDrawings && 'drawings',
    canSeniorManagement && 'senior-management',
    canCustomBuilder && 'custom-builder',
    canSavedReports && 'saved-reports',
  ].filter(Boolean);
  const { projects: contextProjects } = useProjects();
  const [projectFilter, setProjectFilter] = useState('all');
  const [activeTab, setActiveTab] = useState(allowedTabs[0] || 'summary');
  const [customReportEditId, setCustomReportEditId] = useState(null);
  useEffect(() => {
    if (allowedTabs.length && !allowedTabs.includes(activeTab)) {
      setActiveTab(allowedTabs[0]);
    }
  }, [activeTab, allowedTabs.join('|')]);


  const [dataReady, setDataReady] = useState(false);
  const [restDataReady, setRestDataReady] = useState(false);
  const [isFallbackData, setIsFallbackData] = useState(false);
  const [chains, setChains] = useState([]);
  const [raBills, setRaBills] = useState([]);
  const [bankGuarantees, setBankGuarantees] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [deviations, setDeviations] = useState([]);
  const [qaqc, setQaqc] = useState([]);
  const [clearances, setClearances] = useState([]);
  const [drawings, setDrawings] = useState([]);
  const [drawingsLoadError, setDrawingsLoadError] = useState(null);

  // Correspondence register export (print from Reports page)
  const [corrExportStatus, setCorrExportStatus] = useState('all');
  const [corrExportPriority, setCorrExportPriority] = useState('all');
  const [corrExportCategory, setCorrExportCategory] = useState('all');
  const [corrExportDateFrom, setCorrExportDateFrom] = useState('');
  const [corrExportDateTo, setCorrExportDateTo] = useState('');
  const [corrExportSearch, setCorrExportSearch] = useState('');
  const [corrExportBusy, setCorrExportBusy] = useState(false);

  const processChains = (chainsData) =>
    chainsData.map((row) => {
      const mapped = {
        ...row,
        project_id: row.projectId ?? row.project_id,
        category: row.category || 'General',
        severity: row.severity || 'Routine',
        status: row.status || 'Open',
        targeted_reply_days: row.targeted_reply_days ?? row.targetedReplyDays ?? null,
        last_entry_date: row.last_entry_date || row.lastEntryDate || null,
      };
      return {
        ...mapped,
        sla_breached: isChainReplyOverdue(mapped),
      };
    });
  const processRas = (raData) =>
    raData.map((row) => {
      const payload = row.data || {};
      const netPayable = Number(payload.net_payable ?? row.net_payable ?? row.amount ?? 0);
      const paid = Number(payload.paid_value ?? row.paid_value ?? 0);
      return {
        ...payload,
        project_id: row.projectId ?? row.project_id,
        status: row.status || payload.status || 'Submitted',
        payment_status: payload.payment_status ?? row.payment_status ?? 'Unpaid',
        net_payable: netPayable,
        balance_value: Number(payload.balance_value ?? row.balance_value ?? Math.max(0, netPayable - paid)),
      };
    });
  const processBgs = (bgData) =>
    bgData.map((row) => {
      const payload = row.data || {};
      return {
        ...payload,
        id: row.id,
        project_id: row.projectId ?? row.project_id,
        status: row.status || payload.status || 'Active',
        amount: Number(row.amount ?? payload.amount ?? 0),
        expiry_date: payload.expiry_date ?? row.expiry_date,
        bg_no: row.bgNo ?? row.bg_no ?? payload.bg_no ?? `BG-${row.id}`,
        bg_type: payload.bg_type ?? row.bg_type ?? 'Security BG',
        bank: payload.bank ?? row.bank ?? '-',
      };
    });
  const processShifts = (shiftsData) =>
    shiftsData.map((row) => ({
      ...row.data,
      project_id: row.projectId ?? row.project_id,
      status: row.status || row.data?.status || 'Submitted',
      impact_days: Number(row.data?.eot_days_requested || row.data?.impact_days || 0),
    }));
  const processDeviations = (deviationsData) =>
    deviationsData.map((row) => ({
      ...row.data,
      project_id: row.projectId ?? row.project_id,
      status: row.status || row.data?.status || 'Submitted',
      variance_amount: Number(row.data?.variance_amount || 0),
    }));
  const processQaqc = (qaqcData) =>
    qaqcData.map((row) => ({
      ...row.data,
      project_id: row.projectId ?? row.project_id,
      result: row.result || row.data?.result || 'Conditional',
      status: row.status || row.data?.status || 'Under Review',
    }));
  const processClearances = (clearancesData) =>
    clearancesData.map((row) => ({
      ...row.data,
      id: row.id,
      project_id: row.projectId ?? row.project_id,
      status: row.status || row.data?.status || 'Submitted',
      clearance_ref: row.data?.clearance_ref || `CLR-${row.id}`,
      clearance_type: row.data?.clearance_type || row.title || '-',
      authority: row.data?.authority || '-',
      target_date: row.data?.target_date || null,
    }));
  const processDrawings = (drawingsData) =>
    drawingsData.map((row) => {
      const d = row.data || {};
      return {
        id: row.id,
        project_id: row.projectId ?? row.project_id,
        drawing_no: row.drawingNo ?? row.drawing_no ?? d.drawing_no ?? `DWG-${row.id}`,
        title: row.title ?? d.drawing_title ?? '',
        status: row.status ?? d.stage ?? 'Draft',
        stage: d.stage ?? row.status ?? 'Draft',
        discipline: d.discipline ?? '',
        drawing_type: d.drawing_type ?? '',
        revision_no: d.revision_no ?? '',
        revision_date: d.revision_date ?? null,
        submission_date: d.submission_date ?? null,
        approval_date: d.approval_date ?? null,
        required_by_date: d.required_by_date ?? null,
        consultant_name: d.consultant_name ?? '',
        vendor_name: d.vendor_name ?? '',
        latest_revision_flag: d.latest_revision_flag ?? false,
        construction_hold_flag: d.construction_hold_flag ?? false,
        site_impact_level: d.site_impact_level ?? 'None',
      };
    });

  useEffect(() => {
    let cancelled = false;
    const reportQuery = projectFilter && projectFilter !== 'all'
      ? { projectId: projectFilter }
      : {};
    setDataReady(false);
    setRestDataReady(false);

    const loadPhase1 = async () => {
      try {
        const resp = await reportsAPI.getSummaryData(reportQuery);
        const data = resp?.data || {};
        const chainsData = safeArray(data.chains);
        const raData = safeArray(data.raBills);
        if (cancelled) return;
        setChains(processChains(chainsData));
        setRaBills(canFinancials ? processRas(raData) : []);
        setDataReady(true);
        setIsFallbackData(false);
      } catch (_err) {
        try {
          const [chainsResp, raResp] = await Promise.allSettled([
            (canSummary || canCorrespondence)
              ? correspondenceAPI.getChains({ page: 1, pageSize: 2000, ...reportQuery })
              : Promise.resolve({ data: [] }),
            canFinancials
              ? raBillsAPI.getAll({ page: 1, pageSize: 2000, ...reportQuery })
              : Promise.resolve({ data: [] }),
          ]);
          const chainsRaw = chainsResp.status === 'fulfilled' ? chainsResp.value?.data : null;
          const chainsData = Array.isArray(chainsRaw) ? chainsRaw : (chainsRaw?.chains ? safeArray(chainsRaw.chains) : []);
          const raRespData = raResp.status === 'fulfilled' ? raResp.value?.data : null;
          const raData = safeArray(Array.isArray(raRespData) ? raRespData : (raRespData?.data ?? []));
          if (cancelled) return;
          setChains(processChains(chainsData));
          setRaBills(processRas(raData));
          setDataReady(true);
          setIsFallbackData(true);
        } catch (e) {
          if (!cancelled) {
            setDataReady(true);
            setIsFallbackData(true);
          }
        }
      }
    };

    const loadPhase2 = async () => {
      try {
        const resp = await reportsAPI.getRestData(reportQuery);
        const data = resp?.data || {};
        if (cancelled) return;
        setBankGuarantees(canFinancials ? processBgs(safeArray(data.bankGuarantees)) : []);
        setShifts(canFinancials ? processShifts(safeArray(data.milestones)) : []);
        setDeviations(canTechnical ? processDeviations(safeArray(data.deviations)) : []);
        setQaqc(canTechnical ? processQaqc(safeArray(data.qaqc)) : []);
        setClearances(canTechnical ? processClearances(safeArray(data.clearances)) : []);
        try {
          if (!canDrawings) {
            if (!cancelled) {
              setDrawings([]);
              setDrawingsLoadError(null);
            }
            return;
          }
          const dr = await drawingsAPI.getAll({ page: 1, pageSize: 2000, ...reportQuery });
          const drawData = dr?.data;
          const drawRows = safeArray(Array.isArray(drawData) ? drawData : (drawData?.data ?? []));
          if (!cancelled) {
            setDrawings(processDrawings(drawRows));
            setDrawingsLoadError(null);
          }
        } catch (drawErr) {
          if (!cancelled) {
            setDrawings([]);
            setDrawingsLoadError(drawErr?.response?.status === 403
              ? 'Drawings data requires Technical module access. Ask an admin to grant technical:view, or use a role that includes it.'
              : 'Unable to load drawings data for this report.');
          }
        }
      } catch (_err) {
        try {
          const empty = Promise.resolve({ data: [] });
          const [bgResp, shiftsResp, deviationsResp, qaqcResp, clearancesResp, drawingsResp] = await Promise.allSettled([
            canFinancials ? bankGuaranteesAPI.getAll({ page: 1, pageSize: 2000, ...reportQuery }) : empty,
            canFinancials ? milestonesAPI.getAll({ page: 1, pageSize: 2000, ...reportQuery }) : empty,
            canTechnical ? deviationsAPI.getAll({ page: 1, pageSize: 2000, ...reportQuery }) : empty,
            canTechnical ? qaqcAPI.getAll({ page: 1, pageSize: 2000, ...reportQuery }) : empty,
            canTechnical ? clearancesAPI.getAll({ page: 1, pageSize: 2000, ...reportQuery }) : empty,
            canDrawings ? drawingsAPI.getAll({ page: 1, pageSize: 2000, ...reportQuery }) : empty,
          ]);
          const unwrapRows = (r) => {
            const b = r?.data;
            return safeArray(Array.isArray(b) ? b : (b?.data ?? []));
          };
          if (cancelled) return;
          setBankGuarantees(processBgs(unwrapRows(bgResp.status === 'fulfilled' ? bgResp.value : null)));
          setShifts(processShifts(unwrapRows(shiftsResp.status === 'fulfilled' ? shiftsResp.value : null)));
          setDeviations(processDeviations(unwrapRows(deviationsResp.status === 'fulfilled' ? deviationsResp.value : null)));
          setQaqc(processQaqc(unwrapRows(qaqcResp.status === 'fulfilled' ? qaqcResp.value : null)));
          setClearances(processClearances(unwrapRows(clearancesResp.status === 'fulfilled' ? clearancesResp.value : null)));
          if (drawingsResp.status === 'fulfilled') {
            setDrawings(processDrawings(unwrapRows(drawingsResp.value)));
            setDrawingsLoadError(null);
          } else {
            setDrawings([]);
            const st = drawingsResp.reason?.response?.status;
            setDrawingsLoadError(st === 403
              ? 'Drawings data requires Technical module access. Ask an admin to grant technical:view, or use a role that includes it.'
              : 'Unable to load drawings data for this report.');
          }
        } catch (e) {
          if (!cancelled) {
            setBankGuarantees([]);
            setShifts([]);
            setDeviations([]);
            setQaqc([]);
            setClearances([]);
          }
        }
      } finally {
        if (!cancelled) setRestDataReady(true);
      }
    };

    const needsSummaryFetch = canSummary || canCorrespondence || canFinancials;
    const needsRestFetch = canFinancials || canTechnical || canDrawings;
    if (needsSummaryFetch) loadPhase1();
    else setDataReady(true);
    if (needsRestFetch) loadPhase2();
    else setRestDataReady(true);

    return () => { cancelled = true; };
  }, [projectFilter, canSummary, canCorrespondence, canFinancials, canTechnical, canDrawings]);

  const projects = useMemo(() => safeArray(contextProjects).map((p) => ({
    id: p.id,
    name: p.name,
    status: p.status || 'Active',
  })), [contextProjects]);

  const filtered = useMemo(() => {
    const projectId = projectFilter === 'all' ? null : parseInt(projectFilter, 10);
    const byProject = (item) => (projectId ? (item && item.project_id === projectId) : true);

    return {
      chains: safeArray(chains).filter(byProject),
      raBills: safeArray(raBills).filter(byProject),
      bankGuarantees: safeArray(bankGuarantees).filter(byProject),
      shifts: safeArray(shifts).filter(byProject),
      deviations: safeArray(deviations).filter(byProject),
      qaqc: safeArray(qaqc).filter(byProject),
      clearances: safeArray(clearances).filter(byProject),
      drawings: safeArray(drawings).filter(byProject),
    };
  }, [projectFilter, chains, raBills, bankGuarantees, shifts, deviations, qaqc, clearances, drawings]);

  const today = new Date();

  const correspondenceOpen = filtered.chains.filter(c => c.status === 'Open');
  const targetedReplyOverdue = filtered.chains.filter((c) => c.sla_breached || isChainReplyOverdue(c));
  const criticalOpen = correspondenceOpen.filter(c => c.severity === 'Critical');

  const pendingCertification = filtered.raBills.filter(r => r.status === 'Submitted');
  const pendingCertificationValue = pendingCertification.reduce((sum, r) => sum + Number(r.net_payable || 0), 0);
  const raPendingPayment = filtered.raBills.filter(r => r.payment_status === 'Unpaid' || r.payment_status === 'Part Paid');
  const pendingPaymentValue = raPendingPayment.reduce((sum, r) => sum + Number(r.balance_value || 0), 0);

  const bgWithDays = filtered.bankGuarantees.map((bg) => {
    const dte = Math.ceil((new Date(bg.expiry_date) - today) / (1000 * 60 * 60 * 24));
    return { ...bg, dte };
  });
  const bgExpiring30 = bgWithDays.filter(bg => bg.dte > 0 && bg.dte <= 30);
  const bgExpiring60 = bgWithDays.filter(bg => bg.dte > 0 && bg.dte <= 60);
  const upcomingRenewals = [...bgExpiring60].sort((a, b) => a.dte - b.dte).slice(0, 10);

  const shiftsPending = filtered.shifts.filter(s => s.status === 'Submitted' || s.status === 'Under Review');
  const shiftImpactDays = shiftsPending.reduce((sum, s) => sum + Number(s.impact_days || 0), 0);

  const pendingDeviations = filtered.deviations.filter(d => d.status !== 'Approved' && d.status !== 'Rejected');
  const totalVarianceValue = filtered.deviations.reduce((sum, d) => sum + Number(d.variance_amount || 0), 0);

  const qaqcFailOrConditional = filtered.qaqc.filter(q => q.result === 'Fail' || q.result === 'Conditional');

  const pendingClearances = filtered.clearances.filter(c => c.status === 'Draft' || c.status === 'Submitted' || c.status === 'Under Review');
  const overdueClearances = pendingClearances.filter(c => new Date(c.target_date) < today);

  const drawingRegister = filtered.drawings;
  const pendingApprovalDrawings = filtered.drawings.filter(d => ['Submitted', 'Under Review'].includes(d.stage || d.status));
  const overdueDrawings = filtered.drawings.filter(d => {
    const req = d.required_by_date;
    if (!req) return false;
    return new Date(req) < today && !d.approval_date && ['Submitted', 'Under Review'].includes(d.stage || d.status);
  });
  const siteHoldDrawings = filtered.drawings.filter(d => d.construction_hold_flag === true);
  const asBuiltPendingDrawings = filtered.drawings.filter(d => (d.stage || d.status) === 'Approved' && !d.latest_revision_flag);

  const topOverdueThreads = targetedReplyOverdue
    .slice()
    .sort((a, b) => new Date(b.last_entry_date) - new Date(a.last_entry_date))
    .slice(0, 10);

  const categoryChartData = useMemo(() => {
    const map = {};
    safeArray(filtered?.chains).forEach((c) => {
      const key = c?.category || 'General';
      if (!map[key]) map[key] = { category: key, total: 0, open: 0 };
      map[key].total += 1;
      if (c?.status === 'Open') map[key].open += 1;
    });
    return Object.values(map || {})
      .sort((a, b) => b.total - a.total)
      .slice(0, 8);
  }, [filtered.chains]);

  const maxCategoryTotal = Math.max(1, ...categoryChartData.map(item => item.total));
  const projectLabel = useCallback(
    (pid) => stripBrackets(projects.find((p) => p.id === pid)?.name || '') || `Project ${pid}`,
    [projects],
  );

  const outstandingByProject = useMemo(() => {
    const map = {};
    raPendingPayment.forEach((r) => {
      const pid = r.project_id;
      if (!pid) return;
      if (!map[pid]) {
        map[pid] = { projectId: pid, projectName: projectLabel(pid), outstanding: 0, bills: 0 };
      }
      map[pid].outstanding += Number(r.balance_value || 0);
      map[pid].bills += 1;
    });
    return Object.values(map).sort((a, b) => b.outstanding - a.outstanding);
  }, [raPendingPayment, projectLabel]);

  const pendingCertByProject = useMemo(() => {
    const map = {};
    pendingCertification.forEach((r) => {
      const pid = r.project_id;
      if (!pid) return;
      const net = Number(r.net_payable || 0);
      if (net <= 0) return;
      if (!map[pid]) map[pid] = { projectId: pid, projectName: projectLabel(pid), bills: 0, amount: 0 };
      map[pid].amount += net;
      map[pid].bills += 1;
    });
    return Object.values(map).sort((a, b) => b.amount - a.amount);
  }, [pendingCertification, projectLabel]);

  const bgExpiring30ByProject = useMemo(() => {
    const map = {};
    bgExpiring30.forEach((bg) => {
      const pid = Number(bg.project_id);
      const key = pid || 0;
      const amt = Number(bg.amount || 0);
      if (amt <= 0) return;
      const label = pid ? projectLabel(pid) : 'Unassigned / other';
      if (!map[key]) map[key] = { projectId: key, projectName: label, count: 0, amount: 0 };
      map[key].amount += amt;
      map[key].count += 1;
    });
    return Object.values(map).sort((a, b) => b.amount - a.amount);
  }, [bgExpiring30, projectLabel]);

  const shiftsPendingByProject = useMemo(() => {
    const map = {};
    shiftsPending.forEach((s) => {
      const pid = Number(s.project_id);
      if (!pid) return;
      if (!map[pid]) map[pid] = { projectId: pid, projectName: projectLabel(pid), count: 0 };
      map[pid].count += 1;
    });
    return Object.values(map).sort((a, b) => b.count - a.count);
  }, [shiftsPending, projectLabel]);

  const countChainsByProject = useCallback((chainList) => {
    const map = {};
    safeArray(chainList).forEach((c) => {
      const pid = Number(c.project_id);
      if (!pid) return;
      if (!map[pid]) map[pid] = { projectId: pid, projectName: projectLabel(pid), count: 0 };
      map[pid].count += 1;
    });
    return Object.values(map).sort((a, b) => b.count - a.count);
  }, [projectLabel]);

  const openCorrespondenceByProject = useMemo(
    () => countChainsByProject(correspondenceOpen),
    [correspondenceOpen, countChainsByProject],
  );
  const targetedOverdueByProject = useMemo(
    () => countChainsByProject(targetedReplyOverdue),
    [targetedReplyOverdue, countChainsByProject],
  );

  const clearanceOverdueByProject = useMemo(() => {
    const map = {};
    overdueClearances.forEach((c) => {
      const pid = Number(c.project_id);
      if (!pid) return;
      if (!map[pid]) map[pid] = { projectId: pid, projectName: projectLabel(pid), count: 0 };
      map[pid].count += 1;
    });
    return Object.values(map).sort((a, b) => b.count - a.count);
  }, [overdueClearances, projectLabel]);

  const qaqcFailByProject = useMemo(() => {
    const map = {};
    qaqcFailOrConditional.forEach((q) => {
      const pid = Number(q.project_id);
      if (!pid) return;
      if (!map[pid]) map[pid] = { projectId: pid, projectName: projectLabel(pid), count: 0 };
      map[pid].count += 1;
    });
    return Object.values(map).sort((a, b) => b.count - a.count);
  }, [qaqcFailOrConditional, projectLabel]);

  const certifiedUnpaidRAs = useMemo(
    () => filtered.raBills.filter((r) => {
      if (!r) return false;
      const isCertified = String(r.status || '').toLowerCase() === 'certified';
      const isPaid = String(r.payment_status || '').toLowerCase() === 'paid';
      if (!isCertified || isPaid) return false;
      if (!r.payment_due_date) return false;
      const due = new Date(r.payment_due_date);
      return due < today;
    }),
    [filtered.raBills, today],
  );

  const projectsWithPendingEOT = useMemo(
    () => new Set(shiftsPending.map((s) => s.project_id).filter(Boolean)),
    [shiftsPending],
  );

  const bgExpiringSoonWithPendingEOT = useMemo(
    () => bgExpiring30.filter((bg) => bg && projectsWithPendingEOT.has(bg.project_id)),
    [bgExpiring30, projectsWithPendingEOT],
  );

  const bgStatusChartData = useMemo(() => {
    const map = {};
    safeArray(filtered.bankGuarantees).forEach((bg) => {
      const key = (bg && bg.status) || 'Unknown';
      map[key] = (map[key] || 0) + 1;
    });
    return Object.entries(map || {})
      .map(([status, count]) => ({ status, count }))
      .sort((a, b) => b.count - a.count);
  }, [filtered.bankGuarantees]);

  const maxBGStatusCount = Math.max(1, ...bgStatusChartData.map(item => item.count));
  const bgStatusColor = (status) => BG_STATUS_COLOR_MAP[status] || '#94a3b8';
  const getClearanceStatusClass = (status) => CLEARANCE_STATUS_CLASS_MAP[status] || 'draft';
  const scrollToSection = (id) => {
    if (typeof document === 'undefined') return;
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

  const tableFromRows = (headers, rows, amountColumnIndices = []) => {
    const amountSet = new Set(amountColumnIndices);
    const th = (i, h) =>
      amountSet.has(i)
        ? `<th class="amount-cell">${escapeHtml(h)}</th>`
        : `<th>${escapeHtml(h)}</th>`;
    const td = (i, cell) =>
      amountSet.has(i)
        ? `<td class="amount-cell">${escapeHtml(cell)}</td>`
        : `<td>${escapeHtml(cell)}</td>`;
    const thead = `<tr>${headers.map((h, i) => th(i, h)).join('')}</tr>`;
    const tbody = rows.length
      ? rows.map(row => `<tr>${row.map((cell, i) => td(i, cell)).join('')}</tr>`).join('')
      : `<tr><td colspan="${headers.length}" style="text-align:center;color:#6b7280;">No records</td></tr>`;
    return `<table><thead>${thead}</thead><tbody>${tbody}</tbody></table>`;
  };

  const handleDownloadPdf = () => {
    const projectName = projectFilter === 'all'
      ? 'All Projects'
      : (projects.find(p => p.id === parseInt(projectFilter, 10))?.name || 'Selected Project');
    const generatedAt = formatDateTimeIN(new Date());

    let body = '';
    if (activeTab === 'summary') {
      body += '<h3>Critical KPI Summary</h3>';
      body += tableFromRows(
        ['Metric', 'Value', 'Notes'],
        [
          ['Open Correspondence', correspondenceOpen.length, `Critical: ${criticalOpen.length}`],
          ['Targeted Reply Overdue', targetedReplyOverdue.length, 'Needs immediate action'],
          ...(canFinancials ? [
            ['RA Pending Certification', formatCurrency(pendingCertificationValue), `Items: ${pendingCertification.length}`],
            ['BG Expiring (30 Days)', bgExpiring30.length, 'Renewals due'],
            ['RA Pending Payment', formatCurrency(pendingPaymentValue), 'Unpaid / Part Paid balance'],
            ['EOT / Shift Pending', shiftsPending.length, `Impact: ${shiftImpactDays} days`],
          ] : []),
          ...(canTechnical ? [
            ['Clearance Overdue', overdueClearances.length, 'Target date crossed'],
            ['QAQC Fail / Conditional', qaqcFailOrConditional.length, 'Quality attention items'],
          ] : []),
        ],
        [1]
      );
    }

    if (activeTab === 'correspondence') {
      body += '<h3>Overdue Correspondence (Top 10)</h3>';
      body += tableFromRows(
        ['Correspondence Subject', 'Category', 'Priority', 'Status', 'Last letter date'],
        topOverdueThreads.map(c => [c.subject, c.category || 'General', c.severity, c.status, formatDateIN(c.last_entry_date)])
      );

      body += '<h3>Main Categories (Top 8)</h3>';
      body += tableFromRows(
        ['Category', 'Total Threads', 'Open Threads'],
        categoryChartData.map(c => [c.category, c.total, c.open]),
        [1, 2]
      );
    }

    if (activeTab === 'financials') {
      body += '<h3>Upcoming BG Renewals (DTE <= 60)</h3>';
      body += tableFromRows(
        ['BG No', 'Type', 'Bank', 'ValidUpto', 'DTE (days)'],
        upcomingRenewals.map(bg => [bg.bg_no, bg.bg_type, bg.bank, formatDateIN(bg.expiry_date), bg.dte]),
        [4]
      );

      body += '<h3>BG Status Distribution</h3>';
      body += tableFromRows(
        ['Status', 'Count'],
        bgStatusChartData.map(item => [item.status, item.count]),
        [1]
      );
    }

    if (activeTab === 'technical') {
      body += '<h3>Pending Clearances</h3>';
      body += tableFromRows(
        ['Clearance Ref', 'Type', 'Authority', 'Target Date', 'Status'],
        pendingClearances.map(c => [c.clearance_ref, c.clearance_type, c.authority, formatDateIN(c.target_date), c.status])
      );

      body += '<h3>Technical Quality Snapshot</h3>';
      body += tableFromRows(
        ['Metric', 'Value'],
        [
          ['Pending Deviations', pendingDeviations.length],
          ['Total Variance Value', formatCurrency(totalVarianceValue)],
          ['QAQC Fail / Conditional', qaqcFailOrConditional.length],
          ['Pending Clearances', pendingClearances.length],
        ],
        [1]
      );
    }

    if (activeTab === 'drawings') {
      body += '<h3>Drawings & Design – Register (sample)</h3>';
      body += tableFromRows(
        ['Drawing No', 'Title', 'Discipline', 'Type', 'Revision', 'Stage', 'Submission', 'Approval', 'Required By'],
        drawingRegister.slice(0, 50).map(d => [d.drawing_no, d.title || '-', d.discipline || '-', d.drawing_type || '-', d.revision_no || '-', d.stage || d.status, formatDateIN(d.submission_date), formatDateIN(d.approval_date), formatDateIN(d.required_by_date)])
      );
      body += '<h3>Pending Approval</h3>';
      body += tableFromRows(
        ['Drawing No', 'Title', 'Stage', 'Required By'],
        pendingApprovalDrawings.slice(0, 20).map(d => [d.drawing_no, d.title || '-', d.stage || d.status, formatDateIN(d.required_by_date)])
      );
      body += '<h3>Overdue Drawing Response</h3>';
      body += tableFromRows(
        ['Drawing No', 'Title', 'Required By', 'Stage'],
        overdueDrawings.slice(0, 20).map(d => [d.drawing_no, d.title || '-', formatDateIN(d.required_by_date), d.stage || d.status])
      );
      body += '<h3>Site Hold Due to Drawing</h3>';
      body += tableFromRows(
        ['Drawing No', 'Title', 'Discipline', 'Stage'],
        siteHoldDrawings.slice(0, 20).map(d => [d.drawing_no, d.title || '-', d.discipline || '-', d.stage || d.status])
      );
    }

    openPrintWindow({
      title: 'Reports & KPIs',
      subtitle: `Project: ${projectName} | Tab: ${activeTab} | Generated: ${generatedAt}`,
      bodyHtml: body,
    });
  };

  const handlePrintCorrespondenceExport = async () => {
    setCorrExportBusy(true);
    try {
      const params = {
        projectId: projectFilter,
        status: corrExportStatus,
        priority: corrExportPriority,
        category: corrExportCategory,
        dateFrom: corrExportDateFrom || undefined,
        dateTo: corrExportDateTo || undefined,
        search: corrExportSearch || undefined,
        page: 1,
        pageSize: 5000,
      };

      const all = [];
      let page = 1;
      let totalCount = 0;
      const MAX_EXPORT = 20000;

      while (true) {
        const resp = await reportsAPI.getCorrespondenceExport({ ...params, page });
        const payload = resp?.data || {};
        const rows = safeArray(payload.chains);
        totalCount = Number(payload.totalCount || rows.length || 0);
        all.push(...rows);
        if (rows.length === 0) break;
        if (all.length >= totalCount) break;
        if (all.length >= MAX_EXPORT) break;
        page += 1;
      }

      const projectName = projectFilter === 'all'
        ? 'All Projects'
        : (projects.find(p => String(p.id) === String(projectFilter))?.name || `Project ${projectFilter}`);
      const generatedAt = formatDateTimeIN(new Date());
      const subtitleBits = [
        `Project: ${projectName}`,
        corrExportStatus !== 'all' ? `Status: ${corrExportStatus}` : null,
        corrExportPriority !== 'all' ? `Priority: ${corrExportPriority}` : null,
        corrExportCategory !== 'all' ? `Category: ${corrExportCategory}` : null,
        corrExportDateFrom ? `From: ${corrExportDateFrom}` : null,
        corrExportDateTo ? `To: ${corrExportDateTo}` : null,
        corrExportSearch ? `Search: ${corrExportSearch}` : null,
        `Generated: ${generatedAt}`,
      ].filter(Boolean);

      const header = ['Correspondence Subject', 'Category', 'Priority', 'Status', 'Last letter date', 'Entries', 'Letter Ref.'];
      const rows = all.map((c) => ([
        c.subject,
        c.category || 'General',
        c.severity || c.priority || '-',
        c.status || 'Open',
        formatDateIN(c.last_entry_date || c.lastEntryDate || c.lastEntryDateISO),
        String(c.entry_count ?? c.count ?? c.entries_count ?? ''),
        c.letter_refs || '',
      ]));

      const body = [
        '<h3>Correspondence Register Export</h3>',
        `<p style="margin:6px 0 10px;color:#6b7280;font-size:12px;">Rows: ${Math.min(all.length, MAX_EXPORT)}${all.length >= MAX_EXPORT ? ' (capped)' : ''}</p>`,
      ].join('') + (all.length === 0
        ? '<p style="color:#6b7280;font-size:12px;">No records match the selected filters.</p>'
        : `<div>${tableFromRows(header, rows, [5])}</div>`);

      openPrintWindow({
        title: 'Correspondence Register',
        subtitle: subtitleBits.join(' | '),
        bodyHtml: body,
      });
    } finally {
      setCorrExportBusy(false);
    }
  };

  if (!allowedTabs.length) {
    return (
      <div>
        <div className="section-header">
          <h2>Reports & KPIs</h2>
        </div>
        <p style={{ marginTop: 8, fontSize: 13, color: '#6b7280' }}>
          No report categories are assigned to your role. Ask an admin to grant access under Users &amp; Access → Roles (Reports pages).
        </p>
      </div>
    );
  }

  if (!dataReady) {
    return (
      <div>
        <div className="section-header">
          <h2>Reports & KPIs</h2>
        </div>
        <p style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>
          Loading live report data...
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="section-header">
        <h2>Reports & KPIs</h2>
      </div>

      <div className="filter-bar">
        <ProjectFilterSelect
          value={projectFilter}
          onChange={setProjectFilter}
          projects={projects}
        />
        <ResetFiltersButton onClick={() => setProjectFilter('all')} />
        {!['senior-management', 'custom-builder', 'saved-reports'].includes(activeTab) && (
          <button className="btn-primary" onClick={handleDownloadPdf}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Download PDF
          </button>
        )}
      </div>
      {activeTab === 'senior-management' && (
        <p style={{ marginTop: 6, marginBottom: 0, fontSize: 12, color: '#64748b' }}>
          The project filter above applies to Senior Management MI (same as Summary, Correspondence, and other tabs).
        </p>
      )}
      {isFallbackData && (
        <p style={{ marginTop: 8, fontSize: 12, color: '#b45309' }}>
          Unable to load data from API.
        </p>
      )}
      {dataReady && !restDataReady && (canFinancials || canTechnical || canDrawings) && (
        <p style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>
          Loading financial & technical data…
        </p>
      )}

      <div className="status-filter-pills" style={{ marginTop: 8 }}>
        {canSummary && (
        <button className={`filter-pill ${activeTab === 'summary' ? 'active' : ''}`} onClick={() => setActiveTab('summary')}>
          Summary
        </button>
        )}
        {canCorrespondence && (
        <button className={`filter-pill ${activeTab === 'correspondence' ? 'active' : ''}`} onClick={() => setActiveTab('correspondence')}>
          Correspondence
        </button>
        )}
        {canFinancials && (
        <button className={`filter-pill ${activeTab === 'financials' ? 'active' : ''}`} onClick={() => setActiveTab('financials')}>
          Financials & BG
        </button>
        )}
        {canTechnical && (
        <button className={`filter-pill ${activeTab === 'technical' ? 'active' : ''}`} onClick={() => setActiveTab('technical')}>
          Technical & Clearance
        </button>
        )}
        {canDrawings && (
        <button className={`filter-pill ${activeTab === 'drawings' ? 'active' : ''}`} onClick={() => setActiveTab('drawings')}>
          Drawings & Design
        </button>
        )}
        {canSeniorManagement && (
        <button className={`filter-pill ${activeTab === 'senior-management' ? 'active' : ''}`} onClick={() => setActiveTab('senior-management')}>
          Senior Management MI
        </button>
        )}
        {canCustomBuilder && (
          <button className={`filter-pill ${activeTab === 'custom-builder' ? 'active' : ''}`} onClick={() => { setCustomReportEditId(null); setActiveTab('custom-builder'); }}>
            Custom Report Builder
          </button>
        )}
        {canSavedReports && (
          <button className={`filter-pill ${activeTab === 'saved-reports' ? 'active' : ''}`} onClick={() => setActiveTab('saved-reports')}>
            Saved Reports
          </button>
        )}
      </div>

      {canSeniorManagement && activeTab === 'senior-management' && (
        <SeniorManagementReport projectFilter={projectFilter} />
      )}

      {canCustomBuilder && activeTab === 'custom-builder' && (
        <CustomReportBuilder
          editReportId={customReportEditId}
          projectFilter={projectFilter}
          onSaved={() => setActiveTab('saved-reports')}
        />
      )}

      {canSavedReports && activeTab === 'saved-reports' && (
        <SavedCustomReports
          projectFilter={projectFilter}
          onEdit={(id) => { setCustomReportEditId(id); setActiveTab('custom-builder'); }}
        />
      )}

      {canSummary && activeTab === 'summary' && (
      <div style={{ marginTop: 16 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: '#1f2937', marginBottom: 12 }}>Critical KPI Summary</h3>
        <div className="stats-grid">
          <KpiBreakdownTooltip
            title="Open correspondence by project"
            subtitle="Open subjects (threads) per project for the current report filter."
            columns={[
              { key: 'projectName', label: 'Project' },
              { key: 'count', label: 'Open', align: 'right', format: 'int' },
            ]}
            rows={openCorrespondenceByProject}
            emptyMessage="No open correspondence with a linked project."
          >
            <div className="stat-card" onClick={() => setActiveTab('correspondence')} style={{ cursor: 'pointer' }}>
              <div className="stat-icon" style={{ background: '#EDE9FE' }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
              </div>
              <div className="stat-info">
                <p className="stat-label">Open Correspondence</p>
                <p className="stat-value">{correspondenceOpen.length}</p>
                <p className="stat-sub">Critical: {criticalOpen.length}</p>
              </div>
            </div>
          </KpiBreakdownTooltip>
          <KpiBreakdownTooltip
            title="Targeted reply overdue by project"
            subtitle="Open correspondence past SLA / targeted reply date."
            columns={[
              { key: 'projectName', label: 'Project' },
              { key: 'count', label: 'Overdue', align: 'right', format: 'int' },
            ]}
            rows={targetedOverdueByProject}
            emptyMessage="No overdue items with a linked project."
          >
            <div className="stat-card" onClick={() => setActiveTab('correspondence')} style={{ cursor: 'pointer' }}>
              <div className="stat-icon" style={{ background: '#fee2e2' }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              </div>
              <div className="stat-info">
                <p className="stat-label">Targeted Reply Overdue</p>
                <p className="stat-value" style={{ color: '#ef4444' }}>{targetedReplyOverdue.length}</p>
                <p className="stat-sub">Needs immediate action</p>
              </div>
            </div>
          </KpiBreakdownTooltip>
          {canFinancials && (<>
          <KpiBreakdownTooltip
            title="RA pending certification"
            subtitle="Submitted bills with positive net payable, by project."
            columns={[
              { key: 'projectName', label: 'Project' },
              { key: 'bills', label: 'Bills', align: 'right', format: 'int' },
              { key: 'amount', label: 'Net payable (₹)', align: 'right', format: 'inr' },
            ]}
            rows={pendingCertByProject}
            emptyMessage="No submitted RA bills with positive net payable."
          >
            <div className="stat-card" onClick={() => setActiveTab('financials')} style={{ cursor: 'pointer' }}>
              <div className="stat-icon" style={{ background: '#EDE9FE' }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 2h12v20l-2-1.5L14 22l-2-1.5L10 22l-2-1.5L6 22V2z" />
                  <line x1="9" y1="8" x2="15" y2="8" />
                  <line x1="9" y1="12" x2="15" y2="12" />
                </svg>
              </div>
              <div className="stat-info">
                <p className="stat-label">RA Pending Certification</p>
                <p className="stat-value">{formatKpiCrores(pendingCertificationValue)}</p>
                <p className="stat-sub">Items: {pendingCertification.length}</p>
              </div>
            </div>
          </KpiBreakdownTooltip>
          <KpiBreakdownTooltip
            title="BG expiring within 30 days"
            subtitle="By project: count and total BG amount."
            columns={[
              { key: 'projectName', label: 'Project' },
              { key: 'count', label: 'BGs', align: 'right', format: 'int' },
              { key: 'amount', label: 'Amount (₹)', align: 'right', format: 'inr' },
            ]}
            rows={bgExpiring30ByProject}
            emptyMessage="No expiring BGs in this window."
          >
            <div className="stat-card" onClick={() => setActiveTab('financials')} style={{ cursor: 'pointer' }}>
              <div className="stat-icon" style={{ background: '#fef3c7' }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="#b45309" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                  <line x1="1" y1="10" x2="23" y2="10" />
                </svg>
              </div>
              <div className="stat-info">
                <p className="stat-label">BG Expiring (30 Days)</p>
                <p className="stat-value">{bgExpiring30.length}</p>
                <p className="stat-sub">Renewals due</p>
              </div>
            </div>
          </KpiBreakdownTooltip>
          <KpiBreakdownTooltip
            title="RA pending payment"
            subtitle="Unpaid / part-paid bills with balance, by project."
            columns={[
              { key: 'projectName', label: 'Project' },
              { key: 'bills', label: 'Bills', align: 'right', format: 'int' },
              { key: 'outstanding', label: 'Balance (₹)', align: 'right', format: 'inr' },
            ]}
            rows={outstandingByProject}
            emptyMessage="No outstanding balance by project."
          >
            <div className="stat-card" onClick={() => setActiveTab('financials')} style={{ cursor: 'pointer' }}>
              <div className="stat-icon" style={{ background: '#fee2e2' }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              </div>
              <div className="stat-info">
                <p className="stat-label">RA Pending Payment</p>
                <p className="stat-value">{formatKpiCrores(pendingPaymentValue)}</p>
                <p className="stat-sub">Unpaid / Part Paid balance</p>
              </div>
            </div>
          </KpiBreakdownTooltip>
          <KpiBreakdownTooltip
            title="EOT / shift requests pending"
            subtitle="Submitted or under review, by project."
            columns={[
              { key: 'projectName', label: 'Project' },
              { key: 'count', label: 'Requests', align: 'right', format: 'int' },
            ]}
            rows={shiftsPendingByProject}
            emptyMessage="No pending shifts with a linked project."
          >
            <div className="stat-card" onClick={() => setActiveTab('financials')} style={{ cursor: 'pointer' }}>
              <div className="stat-icon" style={{ background: '#EDE9FE' }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                </svg>
              </div>
              <div className="stat-info">
                <p className="stat-label">EOT / Shift Pending</p>
                <p className="stat-value">{shiftsPending.length}</p>
                <p className="stat-sub">Impact: {shiftImpactDays} days</p>
              </div>
            </div>
          </KpiBreakdownTooltip>
          </>)}
          {canTechnical && (<>
          <KpiBreakdownTooltip
            title="Clearance overdue by project"
            subtitle="Pending clearances whose target date has passed."
            columns={[
              { key: 'projectName', label: 'Project' },
              { key: 'count', label: 'Overdue', align: 'right', format: 'int' },
            ]}
            rows={clearanceOverdueByProject}
            emptyMessage="No overdue clearances with a linked project."
          >
            <div className="stat-card" onClick={() => setActiveTab('technical')} style={{ cursor: 'pointer' }}>
              <div className="stat-icon" style={{ background: '#fee2e2' }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </div>
              <div className="stat-info">
                <p className="stat-label">Clearance Overdue</p>
                <p className="stat-value" style={{ color: '#ef4444' }}>{overdueClearances.length}</p>
                <p className="stat-sub">Target date crossed</p>
              </div>
            </div>
          </KpiBreakdownTooltip>
          <KpiBreakdownTooltip
            title="QAQC fail / conditional by project"
            subtitle="Inspections with Fail or Conditional result."
            columns={[
              { key: 'projectName', label: 'Project' },
              { key: 'count', label: 'Items', align: 'right', format: 'int' },
            ]}
            rows={qaqcFailByProject}
            emptyMessage="No QAQC items with a linked project."
          >
            <div className="stat-card" onClick={() => setActiveTab('technical')} style={{ cursor: 'pointer' }}>
              <div className="stat-icon" style={{ background: '#fef3c7' }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="#b45309" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                </svg>
              </div>
              <div className="stat-info">
                <p className="stat-label">QAQC Fail / Conditional</p>
                <p className="stat-value">{qaqcFailOrConditional.length}</p>
                <p className="stat-sub">Quality attention items</p>
              </div>
            </div>
          </KpiBreakdownTooltip>
          </>)}
        </div>
      </div>
      )}

      {canCorrespondence && activeTab === 'correspondence' && (
      <div id="reports-overdue-correspondence" style={{ marginTop: 20 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: '#1f2937', marginBottom: 12 }}>Overdue Correspondence (Top 10)</h3>
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Subject</th>
                <th>Category</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Last letter date</th>
              </tr>
            </thead>
            <tbody>
              {topOverdueThreads.length === 0 ? (
                <tr><td colSpan="5" style={{ textAlign: 'center', padding: 24, color: '#6b7280' }}>No overdue correspondence.</td></tr>
              ) : (
                topOverdueThreads.map((c) => (
                  <tr key={c.id}>
                    <td style={{ fontWeight: 500 }}>{c.subject}</td>
                    <td>{c.category || 'General'}</td>
                    <td>{c.severity}</td>
                    <td><StatusBadge tone={c.status.toLowerCase()}>{c.status}</StatusBadge></td>
                    <td>{formatDateIN(c.last_entry_date)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {canCorrespondence && activeTab === 'correspondence' && (
      <div style={{ marginTop: 20 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: '#1f2937', marginBottom: 12 }}>Main Categories Chart (Top 8)</h3>
        {categoryChartData.length === 0 ? (
          <div className="empty-state">
            <h3>No Category Data</h3>
            <p>Category distribution will appear once correspondence exists.</p>
          </div>
        ) : (
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 14 }}>
            {categoryChartData.map((item) => {
              const totalWidth = `${(item.total / maxCategoryTotal) * 100}%`;
              const openWidth = `${(item.open / maxCategoryTotal) * 100}%`;
              return (
                <div key={item.category} style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                    <span style={{ color: '#374151', fontWeight: 500 }}>{item.category}</span>
                    <span style={{ color: '#6b7280' }}>Total: {item.total} | Open: {item.open}</span>
                  </div>
                  <div style={{ position: 'relative', height: 12, background: '#f3f4f6', borderRadius: 999 }}>
                    <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: totalWidth, background: '#C4B5FD', borderRadius: 999 }} />
                    <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: openWidth, background: '#7C3AED', borderRadius: 999 }} />
                  </div>
                </div>
              );
            })}
            <div style={{ display: 'flex', gap: 16, fontSize: 12, color: '#6b7280', marginTop: 6 }}>
              <span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#C4B5FD', borderRadius: 999, marginRight: 6 }} />Total Threads</span>
              <span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#7C3AED', borderRadius: 999, marginRight: 6 }} />Open Threads</span>
            </div>
          </div>
        )}
      </div>
      )}

      {canCorrespondence && activeTab === 'correspondence' && (
      <div style={{ marginTop: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, color: '#1f2937', margin: 0 }}>Correspondence Register Export</h3>
          <button className="btn-primary" onClick={handlePrintCorrespondenceExport} disabled={corrExportBusy}>
            {corrExportBusy ? 'Preparing…' : 'Print / Download PDF'}
          </button>
        </div>

        <div className="filter-bar" style={{ marginTop: 10, gap: 10, flexWrap: 'wrap' }}>
          <select value={corrExportStatus} onChange={(e) => setCorrExportStatus(e.target.value)} className="input">
            <option value="all">All Status</option>
            <option value="Open">Open</option>
            <option value="Closed">Closed</option>
          </select>

          <select value={corrExportPriority} onChange={(e) => setCorrExportPriority(e.target.value)} className="input">
            <option value="all">All Priority</option>
            <option value="Routine">Routine</option>
            <option value="Urgent">Urgent</option>
            <option value="Critical">Critical</option>
          </select>

          <select value={corrExportCategory} onChange={(e) => setCorrExportCategory(e.target.value)} className="input">
            <option value="all">All Category</option>
            {Array.from(new Set([
              ...safeArray(filtered.chains).map((c) => c.category).filter(Boolean),
              ...(corrExportCategory && corrExportCategory !== 'all' ? [corrExportCategory] : []),
            ]))
              .sort()
              .map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
          </select>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: '#6b7280' }}>Last letter</span>
            <input type="date" value={corrExportDateFrom} onChange={(e) => setCorrExportDateFrom(e.target.value)} className="input" />
            <span style={{ fontSize: 12, color: '#6b7280' }}>to</span>
            <input type="date" value={corrExportDateTo} onChange={(e) => setCorrExportDateTo(e.target.value)} className="input" />
          </div>

          <input
            value={corrExportSearch}
            onChange={(e) => setCorrExportSearch(e.target.value)}
            placeholder="Search subject…"
            className="input"
            style={{ minWidth: 220 }}
          />
          <ResetFiltersButton
            onClick={() => {
              setCorrExportStatus('all');
              setCorrExportPriority('all');
              setCorrExportCategory('all');
              setCorrExportDateFrom('');
              setCorrExportDateTo('');
              setCorrExportSearch('');
            }}
          />
        </div>

        <p style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>
          Opens a preview in a new tab with Print buttons; use your browser to print or save as PDF. Includes all letter reference numbers per subject.
        </p>
      </div>
      )}

      {canFinancials && activeTab === 'financials' && (
      <div style={{ marginTop: 20 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: '#1f2937', marginBottom: 12 }}>RA Bill Reports</h3>
        <div className="table-container" style={{ marginBottom: 16 }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>RA Ref</th>
                <th>Status</th>
                <th>Payment Status</th>
                <th className="amount-cell">Net Payable</th>
                <th className="amount-cell">Balance</th>
              </tr>
            </thead>
            <tbody>
              {pendingCertification.length === 0 && raPendingPayment.length === 0 ? (
                <tr><td colSpan="5" style={{ textAlign: 'center', padding: 24, color: '#6b7280' }}>No pending RA Bills for this project filter.</td></tr>
              ) : (
                [...pendingCertification, ...raPendingPayment].map((r, idx) => (
                  <tr key={`${r.ra_ref || r.ra_no || idx}-${idx}`}>
                    <td style={{ fontWeight: 500, color: '#7C3AED' }}>{r.ra_ref || r.ra_no || '-'}</td>
                    <td><StatusBadge tone={r.status.toLowerCase()}>{r.status}</StatusBadge></td>
                    <td><StatusBadge tone={r.payment_status === 'Paid' ? 'approved' : 'critical'}>{r.payment_status}</StatusBadge></td>
                    <td className="amount-cell">{formatCurrency(r.net_payable || 0)}</td>
                    <td className="amount-cell" style={{ fontWeight: 500, color: (r.balance_value || 0) > 0 ? '#b91c1c' : '#047857' }}>{formatCurrency(r.balance_value || 0)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <h3 style={{ fontSize: 14, fontWeight: 600, color: '#1f2937', marginBottom: 8 }}>Outstanding by Project</h3>
        <div className="table-container" style={{ marginBottom: 24 }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Project</th>
                <th className="amount-cell">Outstanding Balance</th>
              </tr>
            </thead>
            <tbody>
              {outstandingByProject.length === 0 ? (
                <tr><td colSpan="2" style={{ textAlign: 'center', padding: 24, color: '#6b7280' }}>No outstanding RA balances.</td></tr>
              ) : (
                outstandingByProject.map((row) => (
                  <tr key={row.projectId}>
                    <td>
                      <ProjectNameText as="span">{row.projectName}</ProjectNameText>
                    </td>
                    <td className="amount-cell" style={{ fontWeight: 600, color: '#b91c1c' }}>{formatCurrency(row.outstanding)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: '#1f2937', marginBottom: 12 }}>Upcoming BG Renewals (DTE &lt;= 60)</h3>
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>BG No</th>
                <th>Type</th>
                <th>Bank</th>
                <th>ValidUpto</th>
                <th>DTE</th>
              </tr>
            </thead>
            <tbody>
              {upcomingRenewals.length === 0 ? (
                <tr><td colSpan="5" style={{ textAlign: 'center', padding: 24, color: '#6b7280' }}>No BGs expiring in next 60 days.</td></tr>
              ) : (
                upcomingRenewals.map((bg) => (
                  <tr key={bg.id}>
                    <td style={{ fontWeight: 500, color: '#7C3AED' }}>{bg.bg_no}</td>
                    <td>{bg.bg_type}</td>
                    <td>{bg.bank}</td>
                    <td>{formatDateIN(bg.expiry_date)}</td>
                    <td style={{ color: bg.dte <= 30 ? '#b91c1c' : '#b45309', fontWeight: 600 }}>{bg.dte} days</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div style={{ marginTop: 20 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, color: '#1f2937', marginBottom: 12 }}>BG Status Distribution</h3>
          {bgStatusChartData.length === 0 ? (
            <div className="empty-state">
              <h3>No BG Data</h3>
              <p>Status distribution will appear once BG records exist.</p>
            </div>
          ) : (
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 14 }}>
              {bgStatusChartData.map((item) => (
                <div key={item.status} style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                    <span style={{ color: '#374151', fontWeight: 500 }}>{item.status}</span>
                    <span style={{ color: '#6b7280' }}>{item.count}</span>
                  </div>
                  <div style={{ height: 12, background: '#f3f4f6', borderRadius: 999 }}>
                    <div
                      style={{
                        height: '100%',
                        width: `${(item.count / maxBGStatusCount) * 100}%`,
                        background: bgStatusColor(item.status),
                        borderRadius: 999,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div style={{ marginTop: 20 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, color: '#1f2937', marginBottom: 12 }}>Cross-Module Risk Snapshots</h3>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Risk Type</th>
                  <th>Description</th>
                  <th>Count</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={{ fontWeight: 500 }}>Certified RA Unpaid Beyond Due Date</td>
                  <td style={{ fontSize: 13, color: '#4b5563' }}>RA bills with status Certified, payment not marked Paid, and payment due date already crossed.</td>
                  <td style={{ fontWeight: 600, color: certifiedUnpaidRAs.length > 0 ? '#b91c1c' : '#047857' }}>{certifiedUnpaidRAs.length}</td>
                </tr>
                <tr>
                  <td style={{ fontWeight: 500 }}>BG Expiring with Pending EOT</td>
                  <td style={{ fontSize: 13, color: '#4b5563' }}>BG / Bonds expiring in 30 days where the same project also has submitted / under review EOT requests.</td>
                  <td style={{ fontWeight: 600, color: bgExpiringSoonWithPendingEOT.length > 0 ? '#b91c1c' : '#047857' }}>{bgExpiringSoonWithPendingEOT.length}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      )}

      {canTechnical && activeTab === 'technical' && (
      <div id="reports-pending-clearances" style={{ marginTop: 20 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: '#1f2937', marginBottom: 12 }}>Pending Clearances</h3>
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Clearance Ref</th>
                <th>Type</th>
                <th>Authority</th>
                <th>Target Date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {pendingClearances.length === 0 ? (
                <tr><td colSpan="5" style={{ textAlign: 'center', padding: 24, color: '#6b7280' }}>No pending clearances.</td></tr>
              ) : (
                pendingClearances.map((c) => (
                  <tr key={c.id}>
                    <td style={{ fontWeight: 500, color: '#7C3AED' }}>{c.clearance_ref}</td>
                    <td>{c.clearance_type}</td>
                    <td>{c.authority}</td>
                    <td style={{ color: new Date(c.target_date) < today ? '#b91c1c' : '#374151' }}>{formatDateIN(c.target_date)}</td>
                    <td><StatusBadge tone={getClearanceStatusClass(c.status)}>{c.status}</StatusBadge></td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {canTechnical && activeTab === 'technical' && (
      <div id="reports-technical-kpis" style={{ marginTop: 20 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: '#1f2937', marginBottom: 12 }}>Technical Quality Snapshot</h3>
        <div className="kpi-grid">
          <div className="kpi-card" onClick={() => setActiveTab('technical')} style={{ cursor: 'pointer' }}><h4>Pending Deviations</h4><p className="kpi-value">{pendingDeviations.length}</p></div>
          <div className="kpi-card" onClick={() => setActiveTab('technical')} style={{ cursor: 'pointer' }}><h4>Total Variance Value</h4><p className="kpi-value">{formatKpiCrores(totalVarianceValue)}</p></div>
          <div className="kpi-card" onClick={() => setActiveTab('technical')} style={{ cursor: 'pointer' }}><h4>QAQC Fail / Conditional</h4><p className="kpi-value" style={{ color: '#b91c1c' }}>{qaqcFailOrConditional.length}</p></div>
          <div className="kpi-card" onClick={() => { setActiveTab('technical'); setTimeout(() => scrollToSection('reports-pending-clearances'), 0); }} style={{ cursor: 'pointer' }}><h4>Pending Clearances</h4><p className="kpi-value">{pendingClearances.length}</p></div>
        </div>
      </div>
      )}

      {canDrawings && activeTab === 'drawings' && (
      <div style={{ marginTop: 20 }}>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: '#1f2937', marginBottom: 12 }}>Drawings & Design Reports</h3>
        {drawingsLoadError && (
          <p style={{ marginBottom: 12, fontSize: 13, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 12px' }}>
            {drawingsLoadError}
          </p>
        )}
        <div className="stats-grid" style={{ marginBottom: 16 }}>
          <div className="stat-card"><div className="stat-info"><p className="stat-label">Drawing Register Total</p><p className="stat-value">{drawingRegister.length}</p></div></div>
          <div className="stat-card"><div className="stat-info"><p className="stat-label">Pending Approval</p><p className="stat-value" style={{ color: '#b45309' }}>{pendingApprovalDrawings.length}</p></div></div>
          <div className="stat-card"><div className="stat-info"><p className="stat-label">Overdue Response</p><p className="stat-value" style={{ color: '#b91c1c' }}>{overdueDrawings.length}</p></div></div>
          <div className="stat-card"><div className="stat-info"><p className="stat-label">Site Hold (Construction Hold)</p><p className="stat-value">{siteHoldDrawings.length}</p></div></div>
        </div>
        <h3 style={{ fontSize: 14, fontWeight: 600, color: '#1f2937', marginBottom: 8 }}>Drawing Register</h3>
        <div className="table-container" style={{ marginBottom: 16 }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Drawing No</th>
                <th>Title</th>
                <th>Discipline</th>
                <th>Type</th>
                <th>Revision</th>
                <th>Stage</th>
                <th>Submission</th>
                <th>Approval</th>
                <th>Required By</th>
              </tr>
            </thead>
            <tbody>
              {drawingRegister.length === 0 ? (
                <tr><td colSpan="9" style={{ textAlign: 'center', padding: 24, color: '#6b7280' }}>No drawings for selected project.</td></tr>
              ) : (
                drawingRegister.slice(0, 50).map((d) => (
                  <tr key={d.id}>
                    <td style={{ fontWeight: 500, color: '#7C3AED' }}>{d.drawing_no}</td>
                    <td style={{ maxWidth: 200 }}>{d.title || '-'}</td>
                    <td>{d.discipline || '-'}</td>
                    <td>{d.drawing_type || '-'}</td>
                    <td>{d.revision_no || '-'}</td>
                    <td><StatusBadge tone={d.stage === 'Approved' ? 'approved' : d.stage === 'Rejected' ? 'critical' : 'open'}>{d.stage || d.status}</StatusBadge></td>
                    <td>{formatDateIN(d.submission_date)}</td>
                    <td>{formatDateIN(d.approval_date)}</td>
                    <td style={{ color: d.required_by_date && new Date(d.required_by_date) < today && !d.approval_date ? '#b91c1c' : undefined }}>{formatDateIN(d.required_by_date)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <h3 style={{ fontSize: 14, fontWeight: 600, color: '#1f2937', marginBottom: 8 }}>Pending Approval</h3>
        <div className="table-container" style={{ marginBottom: 16 }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Drawing No</th>
                <th>Title</th>
                <th>Stage</th>
                <th>Required By</th>
              </tr>
            </thead>
            <tbody>
              {pendingApprovalDrawings.length === 0 ? (
                <tr><td colSpan="4" style={{ textAlign: 'center', padding: 24, color: '#6b7280' }}>No pending approvals.</td></tr>
              ) : (
                pendingApprovalDrawings.slice(0, 20).map((d) => (
                  <tr key={d.id}>
                    <td style={{ fontWeight: 500 }}>{d.drawing_no}</td>
                    <td>{d.title || '-'}</td>
                    <td>{d.stage || d.status}</td>
                    <td>{formatDateIN(d.required_by_date)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <h3 style={{ fontSize: 14, fontWeight: 600, color: '#1f2937', marginBottom: 8 }}>Overdue Drawing Response</h3>
        <div className="table-container" style={{ marginBottom: 16 }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Drawing No</th>
                <th>Title</th>
                <th>Required By</th>
                <th>Stage</th>
              </tr>
            </thead>
            <tbody>
              {overdueDrawings.length === 0 ? (
                <tr><td colSpan="4" style={{ textAlign: 'center', padding: 24, color: '#6b7280' }}>No overdue drawing responses.</td></tr>
              ) : (
                overdueDrawings.slice(0, 20).map((d) => (
                  <tr key={d.id}>
                    <td style={{ fontWeight: 500, color: '#b91c1c' }}>{d.drawing_no}</td>
                    <td>{d.title || '-'}</td>
                    <td>{formatDateIN(d.required_by_date)}</td>
                    <td>{d.stage || d.status}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <h3 style={{ fontSize: 14, fontWeight: 600, color: '#1f2937', marginBottom: 8 }}>Site Hold Due to Drawing</h3>
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Drawing No</th>
                <th>Title</th>
                <th>Discipline</th>
                <th>Stage</th>
              </tr>
            </thead>
            <tbody>
              {siteHoldDrawings.length === 0 ? (
                <tr><td colSpan="4" style={{ textAlign: 'center', padding: 24, color: '#6b7280' }}>No construction hold drawings.</td></tr>
              ) : (
                siteHoldDrawings.map((d) => (
                  <tr key={d.id}>
                    <td style={{ fontWeight: 500 }}>{d.drawing_no}</td>
                    <td>{d.title || '-'}</td>
                    <td>{d.discipline || '-'}</td>
                    <td>{d.stage || d.status}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}
    </div>
  );
};

export default Reports;

